import Store from '../../store';
import Logger from '../../logger';
import { DeviceOSDetection } from "./os";

const NATIVE_TYPE = {
    CASINO_IOS: 'cbCallBackHandlerCGP',
    CASINO_ANDROID: 'WrapperInterfaceCGP',
    POKER_IOS: 'NativeInterface',
    POKER_ANDROID: 'NativeInterface',
    CIP8_ANDROID: 'pokerNativeInterface',//TODO - will be also used in CGP so that we have a unique source
    SPORT_IOS: 'callbackHandler',
    SPORT_IOS_EMBEDDED: 'sportHybridOnly',
    SPORT_ANDROID: 'WrapperInterface'
};

export class DeviceHybridDetection {
    private logger: Logger;
    private _hybrid = false;
    private _inPoker = false;
    private _inSport = false;
    private _embedded = false;

    constructor(private os: DeviceOSDetection) {
        this.logger = new Logger((this.constructor as any).CLASS);
        this.buildHybridInfo();
    }

    public get isHybrid() {
        return this._hybrid;
    }

    public get inPoker() {
        return this._inPoker;
    }

    public get inSport() {
        return this._inSport;
    }

    public get isEmbedded() {
        return this._embedded;
    }


    /**
     * FOR BUILDING HYBRID INFO I AM BEING EXTRA CAREFULL AND DOUBLECHECKING THE RESULTS FROM DIFFERENT DATA
     */
    private buildHybridInfo() {
        const casinoHybrid = this.build_isCasinoHybrid();
        const inPokerHybrid = this.build_isInPokerHybrid();
        const inSportHybrid = this.build_isInSportHybrid();

        const genericHybridEmbedded = window['cgp_isHybridEmbedded'] || false;
        const casinoHybridEmbedded = window['cgp_isHybridCasinoIos'] || false;
        const inPokerHybridEmbedded = window['cgp_isHybridPokerIos'] || false;
        const inSportHybridEmbedded = window['cgp_isHybridSportIos'] || false;

        if (
            casinoHybrid || inPokerHybrid || inSportHybrid ||
            genericHybridEmbedded || casinoHybridEmbedded || inPokerHybridEmbedded || inSportHybridEmbedded
        ) {
            this._hybrid = true;
        }

        if (genericHybridEmbedded) {
            this._embedded = true;
        }

        if (casinoHybridEmbedded) {
            this._inPoker = false;
            this._inSport = false;
        }

        if (inPokerHybrid || inPokerHybridEmbedded) {
            this._inPoker = true;
        }

        if (inSportHybrid || inSportHybridEmbedded) {
            this._inSport = true;
        }

        this.dataSanityCheck(
            casinoHybrid, inPokerHybrid, inSportHybrid,
            genericHybridEmbedded, casinoHybridEmbedded, inPokerHybridEmbedded, inSportHybridEmbedded
        );
    }

    private build_isCasinoHybrid() {
        // checks for normal casino hybrid
        if (this.os.isIOS) {
            return this.isNativeIOS();
        }

        if (this.os.isAndroid) {
            return this.isNativeAndroid();
        }

        return false;
    }

    private build_isInPokerHybrid() {
        if (this.os.isIOS) {
            // On iOS, first check if poker put a known object in global namespace. If it's there, we know it's poker hybrid, we don't need anything else
            if ((window as any).NativeInterface && (this.isNativeIOS(NATIVE_TYPE.POKER_IOS))) {
                return true;
            }
            // ... otherwise, we have to make an additional check for New Jersey. 103 and 106 are the subbrands of poker in NJ (poker 7, the old one)
            if (['103', '106'].indexOf(Store.get('sourceSubBrand')) === -1) {
                // not NJ, so it's not poker hybrid
                return false;
            }
            // we are in NJ, so now we have to check for the ClientVersion key in storage. If exists and starts with "mp-" or "sp-", then it is poker hybrid
            let storedClientVersion = Store.get('ClientVersion');
            if (storedClientVersion && (storedClientVersion.toLowerCase().startsWith('mp-') || storedClientVersion.toLowerCase().startsWith('sp-'))) {
                return true;
            } else {
                return false;
            }

        }
        if (this.os.isAndroid) {
            // On Android, first check if poker put a known object in global namespace. If it's there, we know it's poker hybrid, we don't need anything else
            if ((window as any)[NATIVE_TYPE.POKER_ANDROID] && this.isNativeAndroid(NATIVE_TYPE.POKER_ANDROID)) {
                return true;
            }
            // Same check for CIP8_ANDROID, because it is a different object name
            if ((window as any)[NATIVE_TYPE.CIP8_ANDROID] && this.isNativeAndroid(NATIVE_TYPE.CIP8_ANDROID)) {
                return true;
            }
            // ... otherwise, we have to make an additional check for New Jersey. 103 and 106 are the subbrands of poker in NJ (poker 7, the old one)
            if (['103', '106'].indexOf(Store.get('sourceSubBrand')) === -1) {
                // not NJ, so it's not poker hybrid
                return false;
            }
            let storedSourceProductPackage = Store.get('sourceProductPackage');
            if (storedSourceProductPackage && (storedSourceProductPackage.toString() === '52' || storedSourceProductPackage.toString() === '95')) {
                return true;
            } else {
                return false;
            }
        }
        return false;
    }

    private build_isInSportHybrid(): boolean {
        if (this.os.isIOS) {
            return this.isNativeIOS(NATIVE_TYPE.SPORT_IOS_EMBEDDED);
        }

        if (this.os.isAndroid) {
            return this.isNativeAndroid(NATIVE_TYPE.SPORT_ANDROID);
        }

        return false;
    }

    /**
     * Is it an IOS Hybrid?
     */
    private isNativeIOS(name?: string): boolean {
        const className = undefined !== name ? name : NATIVE_TYPE.CASINO_IOS;

        return ('object' === typeof ((window as any).webkit)
            && 'object' === typeof ((window as any).webkit.messageHandlers[className]));
    }

    /**
     * Is it an Android Hybrid?
     */
    private isNativeAndroid(name?: string): boolean {
        const className = undefined !== name ? name : NATIVE_TYPE.CASINO_ANDROID;
        return ('object' === typeof ((window as any)[className]));
    }

    private dataSanityCheck(
        casinoHybrid: boolean, inPokerHybrid: boolean, inSportHybrid: boolean,
        genericHybridEmbedded: boolean, casinoHybridEmbedded: boolean, inPokerHybridEmbedded: boolean, inSportHybridEmbedded: boolean
    ) {
        // Casino in Poker and casino in sport cannot be true at the same time
        if (inPokerHybrid === true && inSportHybrid === true) {
            this.logger.error("Hybrid data sanity check. 1. inPokerHybrid and inSportHybrid are both true");
        }

        // From the trio: casinoHybridEmbedded, casinoInPokerHybridEmbedded, casinoInSportHybridEmbedded at most one can be true.
        // And this, only if genericHybridEmbedded is true
        if (genericHybridEmbedded === false && (casinoHybridEmbedded === true || inPokerHybridEmbedded === true || inSportHybridEmbedded === true)) {
            this.logger.error("Hybrid data sanity check. 2. if genericHybridEmbedded is false, none of any other embedded flags can be true");
        }
        if (casinoHybridEmbedded === true && inPokerHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 3. casinoHybridEmbedded and inPokerHybridEmbedded are both true");
        }
        if (casinoHybridEmbedded === true && inSportHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 4. casinoHybridEmbedded and inSportHybridEmbedded are both true");
        }
        if (inPokerHybridEmbedded === true && inSportHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 5. inPokerHybridEmbedded and inSportHybridEmbedded are both true");
        }

        // CASINO: If it's not hybrid it cannot be hybrid embedded
        if (casinoHybrid === false && genericHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 6. genericHybridEmbedded is true but casinoHybrid is false");
        }
        // POKER: If it's not hybrid it cannot be hybrid embedded
        if (inPokerHybrid === false && inPokerHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 7. inPokerHybridEmbedded is true but inPokerHybrid is false");
        }
        // SPORT: If it's not hybrid it cannot be hybrid embedded
        if (inSportHybrid === false && inSportHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 8. inSportHybridEmbedded is true but inSportHybrid is false");
        }

        // In poker and in sport cannot be true at the same time (cross check hybrid and embedded)
        if (inPokerHybrid === true && inSportHybridEmbedded === true) {
            this.logger.error("Hybrid data sanity check. 9. inPokerHybrid and inSportHybridEmbedded are both true");
        }
        if (inPokerHybridEmbedded === true && inSportHybrid === true) {
            this.logger.error("Hybrid data sanity check. 10. inPokerHybridEmbedded and inSportHybrid are both true");
        }
    }
}
